import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import UpgradePrompt from './UpgradePrompt';
import './PersonaSetup.css';
import { useAIConsent } from './utils/useAIConsent';
import AIConsentModal from './AIConsentModal';

const PersonaSetup = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const fileInputRef = useRef(null);
  const voiceInputRef = useRef(null);
  const { consentGiven, showConsentModal, requireConsent, handleAccept, handleDecline } = useAIConsent();

  // Show consent modal on mount if not yet given
  useEffect(() => {
    if (consentGiven === false) {
      requireConsent(() => {}); // trigger modal, no pending action
    }
  }, [consentGiven]);

  // Pro check
  const [isPro, setIsPro] = useState(null);
  // Persona state
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [notification, setNotification] = useState('');
  const [error, setError] = useState('');

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [useCase, setUseCase] = useState('coaching');
  const [tone, setTone] = useState('warm and professional');
  const [coachingStyle, setCoachingStyle] = useState('');
  const [introduction, setIntroduction] = useState('');
  const [signOff, setSignOff] = useState('');
  const [allowedTopics, setAllowedTopics] = useState('');
  const [restrictedTopics, setRestrictedTopics] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [conversationMemoryLimit, setConversationMemoryLimit] = useState(5);
  const [collectInfoOnly, setCollectInfoOnly] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Voice state
  const [voiceStatus, setVoiceStatus] = useState('not_started');
  const [voiceId, setVoiceId] = useState(null);
  const [voiceSamples, setVoiceSamples] = useState([]);
  const [voiceName, setVoiceName] = useState('');
  const [voiceProvider, setVoiceProvider] = useState('openai');
  const [openaiVoice, setOpenaiVoice] = useState('nova');
  const [cloningVoice, setCloningVoice] = useState(false);
  const [playingPreview, setPlayingPreview] = useState(false);
  const audioRef = useRef(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const chunksRef = useRef([]);

  // Knowledge Base state
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docTitle, setDocTitle] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [editingDocNotes, setEditingDocNotes] = useState(null); // doc._id being edited
  const [docNotes, setDocNotes] = useState('');
  const docInputRef = useRef(null);

  // Access tokens state
  const [accessTokens, setAccessTokens] = useState([]);
  const [circleId, setCircleId] = useState(null);
  const [togglingCircle, setTogglingCircle] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [tokenLabel, setTokenLabel] = useState('');
  const [linkSessionDuration, setLinkSessionDuration] = useState(30);
  const [linkSessionFrequency, setLinkSessionFrequency] = useState('1_per_day');
  const [generatingToken, setGeneratingToken] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [newTokenUrl, setNewTokenUrl] = useState('');

  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  // Active section
const [activeSection, setActiveSection] = useState(() => {
  const hash = window.location.hash.replace('#', '');
  return ['setup', 'voice', 'knowledge', 'links', 'conversations'].includes(hash) ? hash : 'setup';
});

  const handleSaveVoiceSettings = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/persona/voice-settings`,
        { provider: voiceProvider, openaiVoice },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotification('Voice settings saved!');
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      setError('Failed to save voice settings');
    }
  };

  // Check Pro status
  useEffect(() => {
    const checkPlan = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsPro(['pro','circle_member','circle_3','circle_5','circle_10'].includes(response.data.plan?.toLowerCase()));
      } catch (error) {
        console.error('Error checking plan:', error);
        setIsPro(false);
      }
    };
    if (token) checkPlan();
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchPersona();
    fetchClients();
    fetchAccessTokens();
    fetchConversations();
    fetchKnowledgeBase();
  }, []);

  const handleCloseConversation = async (conversationId) => {
  if (!window.confirm('Close this conversation? This will generate a summary.')) return;
  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/persona/public/conversation/${conversationId}/end`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setNotification('Conversation closed');
    fetchConversations();
    setTimeout(() => setNotification(''), 3000);
  } catch (err) {
    setError('Failed to close conversation');
  }
};

const handleDeleteConversation = async (conversationId) => {
  if (!window.confirm('Delete this conversation permanently?')) return;
  try {
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/persona/conversations/${conversationId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setNotification('Conversation deleted');
    fetchConversations();
    setTimeout(() => setNotification(''), 3000);
  } catch (err) {
    setError('Failed to delete conversation');
  }
};

  const fetchPersona = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/persona`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.persona) {
        const p = response.data.persona;
        setPersona(p);
        setDisplayName(p.displayName || '');
        setUseCase(p.useCase || 'coaching');
        setTone(p.personality?.tone || 'warm and professional');
        setCoachingStyle(p.personality?.coachingStyle || '');
        setIntroduction(p.personality?.introduction || '');
        setSignOff(p.personality?.signOff || '');
        setAllowedTopics(p.boundaries?.allowedTopics?.join(', ') || '');
        setRestrictedTopics(p.boundaries?.restrictedTopics?.join(', ') || '');
        setCustomInstructions(p.boundaries?.customInstructions || '');
        setConversationMemoryLimit(p.boundaries?.conversationMemoryLimit || 5);
        setCollectInfoOnly(p.boundaries?.collectInfoOnly || false);
        setIsActive(p.isActive || false);
        setProfilePhotoUrl(p.profilePhotoUrl || '');
        setVoiceStatus(p.voiceModel?.status || 'not_started');
        setVoiceId(p.voiceModel?.voiceId || null);
        setVoiceProvider(p.voiceModel?.provider || 'openai');
        setOpenaiVoice(p.voiceModel?.openaiVoice || 'nova');  
      }
    } catch (err) {
      console.error('Error fetching persona:', err);
      setError('Failed to load persona settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchKnowledgeBase = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/persona/knowledge-base`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKnowledgeDocs(response.data.documents || []);
    } catch (err) {
      console.error('Error fetching knowledge base:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchAccessTokens = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/persona/access-tokens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessTokens(response.data.tokens || []);

      // Fetch circle membership to show toggle
      try {
        const circleRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/circles/mine`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (circleRes.data.circle?._id) setCircleId(circleRes.data.circle._id);
      } catch (e) { /* no circle */ }
    } catch (err) {
      console.error('Error fetching access tokens:', err);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/persona/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
      const pending = response.data.conversations?.filter(c => !c.coachReviewed && c.status === 'completed').length || 0;
      setPendingCount(pending);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  };

  const handleSavePersona = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const personaData = {
        displayName: displayName.trim(),
        useCase,
        personality: { tone, coachingStyle: coachingStyle.trim(), introduction: introduction.trim(), signOff: signOff.trim() },
        boundaries: {
          allowedTopics: allowedTopics.split(',').map(t => t.trim()).filter(Boolean),
          restrictedTopics: restrictedTopics.split(',').map(t => t.trim()).filter(Boolean),
          customInstructions: customInstructions.trim(),
          conversationMemoryLimit,
          collectInfoOnly
        },
        isActive
      };
      await axios.post(`${process.env.REACT_APP_API_URL}/api/persona`, personaData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotification('Persona saved successfully!');
      setSaved(true);
      setTimeout(() => {
        setNotification('');
        setSaved(false);
      }, 3000);
      fetchPersona();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save persona');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be less than 5MB'); return; }
    setUploadingPhoto(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/persona/photo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setProfilePhotoUrl(response.data.profilePhotoUrl);
      setNotification('Photo uploaded successfully!');
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/persona/photo`, { headers: { Authorization: `Bearer ${token}` } });
      setProfilePhotoUrl('');
      setNotification('Photo removed');
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      setError('Failed to remove photo');
    }
  };

  const handleVoiceSampleSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + voiceSamples.length > 25) { setError('Maximum 25 audio samples allowed'); return; }
    setVoiceSamples([...voiceSamples, ...files]);
    setError('');
  };

  const removeVoiceSample = (index) => {
    setVoiceSamples(voiceSamples.filter((_, i) => i !== index));
  };

  const handleCloneVoice = async () => {
    if (voiceSamples.length === 0) { setError('Please upload at least one audio sample'); return; }
    setCloningVoice(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('voiceName', voiceName || displayName || 'My Coach Voice');
      formData.append('description', 'Custom coach voice for AirFlyp');
      voiceSamples.forEach((file) => { formData.append('samples', file); });
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/voice/clone`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setVoiceId(response.data.voiceId);
      setVoiceStatus('ready');
      setVoiceSamples([]);
      setNotification('Voice cloned successfully! 🎉');
      setTimeout(() => setNotification(''), 5000);
      fetchPersona();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clone voice');
      setVoiceStatus('failed');
    } finally {
      setCloningVoice(false);
    }
  };

  const handleDeleteVoice = async () => {
    if (!window.confirm('Delete your cloned voice?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/voice/clone`, { headers: { Authorization: `Bearer ${token}` } });
      setVoiceId(null);
      setVoiceStatus('not_started');
      setNotification('Voice deleted');
      setTimeout(() => setNotification(''), 3000);
      fetchPersona();
    } catch (err) {
      setError('Failed to delete voice');
    }
  };

  const handlePreviewVoice = async () => {
    if (!voiceId) return;
    setPlayingPreview(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/voice/preview/${voiceId}`, { headers: { Authorization: `Bearer ${token}` } });
      const audio = new Audio(`data:audio/mpeg;base64,${response.data.audio}`);
      audioRef.current = audio;
      audio.onended = () => setPlayingPreview(false);
      audio.play();
    } catch (err) {
      setError('Failed to play voice preview');
      setPlayingPreview(false);
    }
  };

  const stopPreview = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingPreview(false);
  };

  // Voice Recording handlers
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const discardRecording = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
  };

  const useRecordingAsVoiceSample = () => {
    if (recordedBlob) {
      const file = new File([recordedBlob], `voice-recording-${Date.now()}.webm`, { type: 'audio/webm' });
      setVoiceSamples(prev => [...prev, file]);
      discardRecording();
      setNotification('Recording added to voice samples!');
      setTimeout(() => setNotification(''), 3000);
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Knowledge Base handlers
  const ALLOWED_DOC_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword'
  ];

  const processDocUpload = async (file) => {
    if (!file) return;
    
    if (!ALLOWED_DOC_TYPES.includes(file.type)) {
      setError('Only PDF and Word documents are allowed');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be under 20MB');
      return;
    }

    setUploadingDoc(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('title', docTitle.trim() || file.name.replace(/\.(pdf|docx?)$/i, ''));
      
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/persona/knowledge-base`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      setNotification('Document uploaded successfully!');
      setDocTitle('');
      fetchKnowledgeBase();
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDocUpload = (e) => {
    processDocUpload(e.target.files?.[0]);
  };

  const handleDocDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    processDocUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document from your knowledge base?')) return;
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/persona/knowledge-base/${docId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotification('Document deleted');
      fetchKnowledgeBase();
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      setError('Failed to delete document');
    }
  };

  const handleSaveDocNotes = async (docId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/persona/knowledge-base/${docId}/notes`,
        { notes: docNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotification('Notes saved');
      setEditingDocNotes(null);
      setDocNotes('');
      fetchKnowledgeBase();
      setTimeout(() => setNotification(''), 3000);
    } catch (err) {
      setError('Failed to save notes');
    }
  };

  const handleGenerateToken = async () => {
    if (!selectedClientId) { setError('Please select a client'); return; }
    if (!persona?.isActive) { setError('Please activate your persona first'); return; }
    setGeneratingToken(true);
    setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/persona/access-token`, { 
        clientId: selectedClientId, 
        label: tokenLabel.trim() || undefined,
        sessionDuration: linkSessionDuration,
        sessionFrequency: linkSessionFrequency
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewTokenUrl(response.data.accessUrl);
      setShowTokenModal(true);
      setSelectedClientId('');
      setTokenLabel('');
      setLinkSessionDuration(30); // Reset to default
      setLinkSessionFrequency('1_per_day'); // Reset to default
      fetchAccessTokens();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate link');
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleToggleCircleVisible = async (tokenId, currentValue) => {
    setTogglingCircle(tokenId);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/circles/persona-tokens/${tokenId}/visibility`,
        { visible: !currentValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccessTokens(prev => prev.map(t =>
        t._id === tokenId ? { ...t, circleVisible: !currentValue } : t
      ));
    } catch (err) {
      console.error('[PersonaSetup] toggle circle visible:', err);
    } finally {
      setTogglingCircle(null);
    }
  };

  const handleDeactivateToken = async (tokenId) => {
    if (!window.confirm('Deactivate this link?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/persona/access-token/${tokenId}`, { headers: { Authorization: `Bearer ${token}` } });
      setNotification('Link deactivated');
      setTimeout(() => setNotification(''), 3000);
      fetchAccessTokens();
    } catch (err) {
      setError('Failed to deactivate link');
    }
  };

  const [copiedUrl, setCopiedUrl] = useState(null);

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setNotification('Link copied to clipboard!');
    setTimeout(() => {
      setNotification('');
      setCopiedUrl(null);
    }, 3000);
  };

  const handleMarkReviewed = async (conversationId) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/persona/conversations/${conversationId}/review`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotification('Marked as reviewed');
      setTimeout(() => setNotification(''), 3000);
      fetchConversations();
    } catch (err) {
      setError('Failed to mark as reviewed');
    }
  };

  if (loading) {
    return <div className="persona-setup-container"><div className="loading-spinner">Loading...</div></div>;
  }

  if (isPro === false) {
    return <UpgradePrompt feature="AI Persona" />;
  }

  return (
    <div className="persona-setup-container">
      <div className="persona-header">
        <div className="header-top">
          <button className="back-to-dashboard" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        </div>
        <h1>🤖 AI Persona Setup</h1>
        <p>Create an AI assistant that represents you to your clients</p>
      </div>

      {notification && <div className="notification success">{notification}</div>}
      {error && <div className="notification error">{error}</div>}

      <div className="persona-tabs">
        <button className={`tab-btn ${activeSection === 'setup' ? 'active' : ''}`} onClick={() => { setActiveSection('setup'); window.location.hash = 'setup'; }}>⚙️ Setup</button>
        <button className={`tab-btn ${activeSection === 'voice' ? 'active' : ''}`} onClick={() => { setActiveSection('voice'); window.location.hash = 'voice'; }}>🎙️ Voice {voiceStatus === 'ready' && <span className="badge green">✓</span>}</button>
        <button className={`tab-btn ${activeSection === 'knowledge' ? 'active' : ''}`} onClick={() => { setActiveSection('knowledge'); window.location.hash = 'knowledge'; }}>📚 Knowledge {knowledgeDocs.length > 0 && <span className="badge green">{knowledgeDocs.length}</span>}</button>
        <button className={`tab-btn ${activeSection === 'links' ? 'active' : ''}`} onClick={() => { setActiveSection('links'); window.location.hash = 'links'; }}>🔗 Client Links</button>
        <button className={`tab-btn ${activeSection === 'conversations' ? 'active' : ''}`} onClick={() => { setActiveSection('conversations'); window.location.hash = 'conversations'; }}>💬 Conversations {pendingCount > 0 && <span className="badge">{pendingCount}</span>}</button>
      </div>

      {activeSection === 'setup' && (
        <div className="persona-section">
          <form onSubmit={(e) => { e.preventDefault(); requireConsent(() => handleSavePersona(e)); }}>
            <div className="form-card">
              <h2>📸 Profile Photo</h2>
              <p className="form-description">This photo will be shown to clients when they chat with your AI</p>
              <div className="photo-upload-area">
                {profilePhotoUrl ? (
                  <div className="photo-preview">
                    <img src={profilePhotoUrl} alt="Profile" />
                    <button type="button" className="remove-photo-btn" onClick={handleRemovePhoto}>✕ Remove</button>
                  </div>
                ) : (
                  <div className="photo-placeholder" onClick={() => fileInputRef.current?.click()}>
                    <span>📷</span>
                    <p>Click to upload photo</p>
                  </div>
                )}
                <input type="file" ref={fileInputRef} accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                {!profilePhotoUrl && <button type="button" className="upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}>{uploadingPhoto ? 'Uploading...' : 'Upload Photo'}</button>}
              </div>
            </div>

            <div className="form-card">
              <h2>🏷️ Identity</h2>
              <div className="form-group">
                <label>Display Name *</label>
                <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g., Coach Mario's AI Assistant" required />
                <span className="helper-text">How your AI will introduce itself</span>
              </div>
              <div className="form-group">
                <label>Use Case</label>
                <select value={useCase} onChange={(e) => {
                  setUseCase(e.target.value);
                  // Auto-set recommended tone based on use case
                  if (e.target.value === 'coaching') {
                    setTone('warm and professional');
                  } else if (e.target.value === 'sales') {
                    setTone('persuasive and consultative');
                  } else if (e.target.value === 'support') {
                    setTone('helpful and patient');
                  } else if (e.target.value === 'training') {
                    setTone('encouraging and instructive');
                  }
                }}>
                  <option value="coaching">🎯 Coaching</option>
                  <option value="sales">💼 Sales</option>
                  <option value="support">🤝 Customer Support</option>
                  <option value="training">📚 Training & Education</option>
                  <option value="consulting">💡 Consulting</option>
                  <option value="other">⚡ Other</option>
                </select>
                <span className="helper-text">This helps customize the AI's behavior</span>
              </div>
              <div className="form-group toggle-group">
                <label><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /><span className="toggle-label">Activate AI Persona</span></label>
                <span className="helper-text">{isActive ? '✅ Your AI is active and can receive clients' : '⏸️ Your AI is currently inactive'}</span>
              </div>
            </div>

            <div className="form-card">
              <h2>🎭 Personality</h2>
              <div className="form-group">
                <label>Tone</label>
                <select value={tone} onChange={(e) => setTone(e.target.value)}>
                  <optgroup label="General">
                    <option value="warm and professional">Warm and Professional</option>
                    <option value="friendly and casual">Friendly and Casual</option>
                    <option value="formal and structured">Formal and Structured</option>
                    <option value="direct and action-oriented">Direct and Action-Oriented</option>
                  </optgroup>
                  <optgroup label="Coaching">
                    <option value="encouraging and supportive">Encouraging and Supportive</option>
                    <option value="challenging and growth-focused">Challenging and Growth-Focused</option>
                    <option value="empathetic and reflective">Empathetic and Reflective</option>
                  </optgroup>
                  <optgroup label="Sales">
                    <option value="persuasive and consultative">Persuasive and Consultative</option>
                    <option value="confident and closing-oriented">Confident and Closing-Oriented</option>
                    <option value="relationship-building">Relationship-Building</option>
                    <option value="energetic and enthusiastic">Energetic and Enthusiastic</option>
                  </optgroup>
                  <optgroup label="Support">
                    <option value="helpful and patient">Helpful and Patient</option>
                    <option value="solution-focused">Solution-Focused</option>
                  </optgroup>
                  <optgroup label="Training">
                    <option value="encouraging and instructive">Encouraging and Instructive</option>
                    <option value="clear and methodical">Clear and Methodical</option>
                  </optgroup>
                </select>
              </div>
              <div className="form-group">
                <label>{useCase === 'sales' ? 'Sales Approach' : useCase === 'support' ? 'Support Style' : 'Coaching Style'}</label>
                <input type="text" value={coachingStyle} onChange={(e) => setCoachingStyle(e.target.value)} placeholder={
                  useCase === 'sales' ? "e.g., Consultative selling, SPIN selling, Challenger" :
                  useCase === 'support' ? "e.g., Empathetic, Technical, Step-by-step" :
                  "e.g., Solution-focused, Socratic questioning"
                } />
              </div>
              <div className="form-group">
                <label>Custom Greeting</label>
                <textarea value={introduction} onChange={(e) => setIntroduction(e.target.value)} placeholder={
                  useCase === 'sales' ? "e.g., Hi! I'm here to help you explore solutions that fit your needs..." :
                  useCase === 'support' ? "e.g., Hello! I'm here to help resolve any issues you're experiencing..." :
                  "e.g., Hi! I'm Coach Mario's AI assistant..."
                } rows={3} />
              </div>
              <div className="form-group">
                <label>Sign-Off Message</label>
                <input type="text" value={signOff} onChange={(e) => setSignOff(e.target.value)} placeholder={
                  useCase === 'sales' ? "e.g., Looking forward to helping you succeed!" :
                  useCase === 'support' ? "e.g., Let me know if there's anything else I can help with!" :
                  "e.g., Remember, your coach is always here to support you!"
                } />
              </div>
            </div>

            <div className="form-card">
              <h2>🚧 Boundaries</h2>
              <div className="form-group">
                <label>Allowed Topics</label>
                <input type="text" value={allowedTopics} onChange={(e) => setAllowedTopics(e.target.value)} placeholder="e.g., goals, progress, accountability" />
                <span className="helper-text">Comma-separated</span>
              </div>
              <div className="form-group">
                <label>Restricted Topics</label>
                <input type="text" value={restrictedTopics} onChange={(e) => setRestrictedTopics(e.target.value)} placeholder="e.g., billing, legal advice" />
                <span className="helper-text">Topics redirected to you</span>
              </div>
              <div className="form-group">
                <label>Custom Instructions</label>
                <textarea value={customInstructions} onChange={(e) => setCustomInstructions(e.target.value)} placeholder="Add your bio, coaching philosophy, frameworks..." rows={6} />
                <span className="helper-text">The more context, the better your AI represents you</span>
              </div>
              <div className="form-group">
                <label>Conversation Memory</label>
                <select value={conversationMemoryLimit} onChange={(e) => setConversationMemoryLimit(Number(e.target.value))}>
                  <option value={3}>Remember last 3 conversations</option>
                  <option value={5}>Remember last 5 conversations</option>
                  <option value={10}>Remember last 10 conversations</option>
                </select>
              </div>
              <div className="form-group toggle-group">
                <label><input type="checkbox" checked={collectInfoOnly} onChange={(e) => setCollectInfoOnly(e.target.checked)} /><span className="toggle-label">Information Gathering Mode</span></label>
                <span className="helper-text">AI only collects info, no coaching advice</span>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className={`save-btn ${saved ? 'saved' : ''}`} disabled={saving}>{saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Persona'}</button>
              <button type="button" className="back-btn" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
            </div>
          </form>
        </div>
      )}

      {activeSection === 'voice' && (
        <div className="persona-section">
          <div className="form-card">
            <h2>🎙️ Voice Cloning</h2>
            <p className="form-description">Clone your voice so your AI sounds like you! Upload 1-3 minutes of clear audio.</p>

            {voiceStatus === 'ready' && voiceId ? (
              <div className="voice-ready">
                <div className="voice-status-banner success">
                  <span>✅</span>
                  <div><strong>Your voice is ready!</strong><p>Your AI will speak in your cloned voice.</p></div>
                </div>
                <div className="voice-actions">
                  <button className="preview-btn" onClick={playingPreview ? stopPreview : handlePreviewVoice}>{playingPreview ? '⏹️ Stop' : '▶️ Preview Voice'}</button>
                  <button className="delete-voice-btn" onClick={handleDeleteVoice}>🗑️ Delete Voice</button>
                </div>
              </div>
            ) : voiceStatus === 'processing' ? (
              <div className="voice-status-banner processing"><span>⏳</span><div><strong>Processing...</strong><p>Usually takes 1-2 minutes.</p></div></div>
            ) : (
              <div className="voice-upload">
                <div className="upload-instructions">
                  <h3>Tips for best results:</h3>
                  <ul>
                    <li>📢 Speak clearly and naturally</li>
                    <li>🔇 Quiet environment, no background noise</li>
                    <li>🎯 1-3 minutes total audio</li>
                    <li>💬 Conversational tone works best</li>
                  </ul>
                </div>
              <div className="form-group">
                  <label>Voice Type</label>
                  <select value={voiceProvider} onChange={(e) => setVoiceProvider(e.target.value)}>
                    <option value="openai">Standard AI Voice (Free)</option>
                    <option value="elevenlabs">Clone My Voice (Premium)</option>
                  </select>
                  <span className="helper-text">{voiceProvider === 'openai' ? 'Use a high-quality AI voice' : 'Clone your own voice for a personalized experience'}</span>
                </div>

              {voiceProvider === 'openai' && (
                  <>
                    <div className="form-group">
                      <label>Select Voice</label>
                      <select value={openaiVoice} onChange={(e) => setOpenaiVoice(e.target.value)}>
                        <option value="nova">Nova (Female)</option>
                        <option value="shimmer">Shimmer (Female)</option>
                        <option value="alloy">Alloy (Neutral)</option>
                        <option value="echo">Echo (Male)</option>
                        <option value="fable">Fable (Male)</option>
                        <option value="onyx">Onyx (Male)</option>
                      </select>
                    </div>
                    <button type="button" className="save-btn" onClick={handleSaveVoiceSettings}>
                      💾 Save Voice Settings
                    </button>
                  </>
                )}

                {voiceProvider === 'elevenlabs' && (
                  <div className="form-group">
                    <label>Voice Name (optional)</label>
                    <input type="text" value={voiceName} onChange={(e) => setVoiceName(e.target.value)} placeholder={displayName || "My Coach Voice"} />
                  </div>
                )}

                {voiceProvider === 'elevenlabs' && (
                <>
                {/* Voice Recorder */}
                <div className="voice-recorder-section">
                  <h4>🎤 Record Your Voice</h4>
                  <p className="recorder-hint">Record directly from your device. Aim for 1-3 minutes of natural speech.</p>
                  
                  {!recordedBlob ? (
                    <div className="recorder-controls">
                      {!isRecording ? (
                        <button type="button" className="record-btn" onClick={startRecording}>
                          🎙️ Start Recording
                        </button>
                      ) : (
                        <div className="recording-active">
                          <div className="recording-indicator">
                            <span className="recording-dot"></span>
                            <span className="recording-time">{formatRecordingTime(recordingTime)}</span>
                          </div>
                          <button type="button" className="stop-record-btn" onClick={stopRecording}>
                            ⏹️ Stop Recording
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="recording-preview">
                      <div className="recording-info">
                        <span>🎵 Recording ({formatRecordingTime(recordingTime)})</span>
                        <audio src={recordedUrl} controls className="recording-audio" />
                      </div>
                      <div className="recording-actions">
                        <button type="button" className="use-recording-btn" onClick={useRecordingAsVoiceSample}>
                          ✓ Use This Recording
                        </button>
                        <button type="button" className="discard-recording-btn" onClick={discardRecording}>
                          ✕ Discard
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="voice-divider">
                  <span>or upload files</span>
                </div>

                <div className="voice-samples-area">
                  <input type="file" ref={voiceInputRef} accept="audio/*" multiple onChange={handleVoiceSampleSelect} style={{ display: 'none' }} />
                  <button type="button" className="upload-samples-btn" onClick={() => voiceInputRef.current?.click()}>📁 Select Audio Files</button>
                  {voiceSamples.length > 0 && (
                    <div className="samples-list">
                      <h4>Selected ({voiceSamples.length})</h4>
                      {voiceSamples.map((file, index) => (
                        <div key={index} className="sample-item"><span>🎵 {file.name}</span><button type="button" className="remove-sample" onClick={() => removeVoiceSample(index)}>✕</button></div>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" className="clone-btn" onClick={handleCloneVoice} disabled={cloningVoice || voiceSamples.length === 0}>{cloningVoice ? '⏳ Cloning...' : '🎙️ Clone My Voice'}</button>
                {voiceStatus === 'failed' && <div className="voice-status-banner error"><span>❌</span><div><strong>Failed</strong><p>Try different audio samples.</p></div></div>}
                </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeSection === 'knowledge' && (
        <div className="persona-section">
          <div className="form-card">
            <h2>📚 Knowledge Base</h2>
            <p className="form-description">Upload PDF or Word documents to give your AI reference material. Your AI will use this information to provide more accurate and informed responses.</p>
            
            <div 
              className={`knowledge-upload-area ${dragOver ? 'drag-over' : ''}`}
              onDrop={handleDocDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className="form-group">
                <label>Document Title (optional)</label>
                <input 
                  type="text" 
                  value={docTitle} 
                  onChange={(e) => setDocTitle(e.target.value)} 
                  placeholder="e.g., Sales Playbook, FAQ, Product Guide" 
                />
              </div>
              <input 
                type="file" 
                ref={docInputRef} 
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" 
                onChange={handleDocUpload} 
                style={{ display: 'none' }} 
              />
              <div className="drop-zone">
                <p className="drop-text">{dragOver ? '📥 Drop file here!' : '📄 Drag & drop a file here'}</p>
                <p className="drop-or">or</p>
                <button 
                  type="button" 
                  className="upload-doc-btn" 
                  onClick={() => docInputRef.current?.click()} 
                  disabled={uploadingDoc}
                >
                  {uploadingDoc ? '⏳ Uploading...' : '📁 Browse Files'}
                </button>
              </div>
              <p className="upload-hint">Max file size: 20MB. PDF and Word documents (.doc, .docx) are supported.</p>
            </div>
          </div>

          <div className="form-card">
            <h2>📁 Uploaded Documents</h2>
            {knowledgeDocs.length === 0 ? (
              <p className="no-data">No documents uploaded yet. Upload PDFs or Word docs to give your AI reference material.</p>
            ) : (
              <div className="knowledge-docs-list">
          {knowledgeDocs.map(doc => (
                  <div key={doc._id} className="knowledge-doc-item">
                    <div className="doc-info">
                      <span className="doc-icon">📄</span>
                      <div className="doc-details">
                        <strong>{doc.title}</strong>
                        <span className="doc-meta">
                          {Math.round(doc.textLength / 1000)}k chars • Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                        </span>
                        <p className="doc-preview">{doc.preview}</p>
                        
                        {editingDocNotes === doc._id ? (
                          <div className="doc-notes-edit">
                            <textarea
                              value={docNotes}
                              onChange={(e) => setDocNotes(e.target.value)}
                              placeholder="e.g., Use for pricing questions only, Pull objection handling scripts from here..."
                              rows={2}
                            />
                            <div className="doc-notes-actions">
                              <button type="button" onClick={() => handleSaveDocNotes(doc._id)}>💾 Save</button>
                              <button type="button" onClick={() => { setEditingDocNotes(null); setDocNotes(''); }}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="doc-notes-display">
                            {doc.notes ? (
                              <p className="doc-notes"><strong>Usage:</strong> {doc.notes}</p>
                            ) : null}
                            <button 
                              type="button" 
                              className="edit-notes-btn"
                              onClick={() => { setEditingDocNotes(doc._id); setDocNotes(doc.notes || ''); }}
                            >
                              {doc.notes ? '✏️ Edit Rules' : '➕ Add Usage Rules'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <button 
                      type="button" 
                      className="delete-doc-btn" 
                      onClick={() => handleDeleteDoc(doc._id)}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeSection === 'links' && (
        <div className="persona-section">
          <div className="form-card">
            <h2>🔗 Generate Client Link</h2>
            <p className="form-description">Create a unique link for a client to chat with your AI</p>
            <div className="generate-link-form">
              <div className="form-group">
                <label>Select Client</label>
                <select value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)}>
                  <option value="">-- Select a client --</option>
                  {clients.map(client => <option key={client._id} value={client._id}>{client.firstName} {client.lastName}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Label (optional)</label>
                <input type="text" value={tokenLabel} onChange={(e) => setTokenLabel(e.target.value)} placeholder="e.g., Weekly check-in" />
              </div>
              <div className="form-row">
                <div className="form-group half">
                  <label>Session Duration</label>
                  <select value={linkSessionDuration} onChange={(e) => setLinkSessionDuration(Number(e.target.value))}>
                    <option value={15}>15 minutes</option>
                    <option value={20}>20 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                </div>
                <div className="form-group half">
                  <label>Session Frequency</label>
                <select value={linkSessionFrequency} onChange={(e) => setLinkSessionFrequency(e.target.value)}>
                    <option value="unlimited">Unlimited</option>
                    <option value="1_per_day">1x per day</option>
                    <option value="1_per_week">1x per week</option>
                    <option value="1_per_month">1x per month</option>
                  </select>
                </div>
              </div>
              <button type="button" className="generate-btn" onClick={handleGenerateToken} disabled={generatingToken || !selectedClientId}>{generatingToken ? 'Generating...' : '✨ Generate Link'}</button>
            </div>
          </div>
          <div className="form-card">
            <h2>📋 Active Links</h2>
            {accessTokens.length === 0 ? <p className="no-data">No links generated yet</p> : (
              <div className="tokens-list">
                {accessTokens.filter(t => t.isActive).map(tkn => (
                  <div key={tkn._id} className="token-item">
                    <div className="token-info">
                      <span className="client-name">{tkn.clientId?.firstName} {tkn.clientId?.lastName}</span>
                      {tkn.label && <span className="token-label">{tkn.label}</span>}
                      <span className="token-date">Created: {new Date(tkn.createdAt).toLocaleDateString()}</span>
                      <span className="token-usage">Used: {tkn.conversationsUsed} times</span>
                      <span className="token-limits">⏱️ {tkn.sessionDuration || 30}min • {tkn.sessionFrequency === 'unlimited' ? 'Unlimited' : tkn.sessionFrequency === '1_per_day' ? '1x/day' : tkn.sessionFrequency === '1_per_week' ? '1x/week' : '1x/month'}</span>
                    </div>
                    <div className="token-actions">
                      <button className={`copy-btn ${copiedUrl === `https://app.airflyp.com/ai/${tkn.token}` ? 'copied' : ''}`} onClick={() => handleCopyLink(`https://app.airflyp.com/ai/${tkn.token}`)}>
                        {copiedUrl === `https://app.airflyp.com/ai/${tkn.token}` ? '✓ Copied!' : '📋 Copy'}
                      </button>
                      {circleId && (
                        <button
                          className={`circle-toggle-btn ${tkn.circleVisible ? 'active' : ''}`}
                          onClick={() => handleToggleCircleVisible(tkn._id, tkn.circleVisible)}
                          disabled={togglingCircle === tkn._id}
                          title={tkn.circleVisible ? 'Remove from Circle' : 'Share with Circle'}
                        >
                          {togglingCircle === tkn._id ? '...' : tkn.circleVisible ? '✦ Circle ON' : '✦ Circle OFF'}
                        </button>
                      )}
                      <button className="deactivate-btn" onClick={() => handleDeactivateToken(tkn._id)}>🚫 Deactivate</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

{activeSection === 'conversations' && (
        <div className="persona-section">
          <div className="form-card">
            <h2>🟢 In Progress</h2>
            {conversations.filter(c => c.status === 'active').length === 0 ? (
              <p className="no-data">No active conversations</p>
            ) : (
              <div className="conversations-list">
                {conversations
                  .filter(c => c.status === 'active')
                  .sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))
                  .map(conv => (
                  <div key={conv._id} className="conversation-item">
                    <div className="conversation-header">
                      <span className="client-name">{conv.clientId?.firstName} {conv.clientId?.lastName}</span>
                      <span className={`status-badge ${conv.status}`}>{conv.status}</span>
                    </div>
                    <div className="conversation-meta">
                      <span>📅 {new Date(conv.startedAt).toLocaleDateString()}</span>
                      <span>⏱️ {conv.duration || 0} min</span>
                      <span>💬 {conv.messages?.length || 0} messages</span>
                    </div>
                  <div className="conversation-actions">
                    <button className="view-btn" onClick={() => navigate(`/persona/conversation/${conv._id}`)}>👁️ View</button>
                    <button className="review-btn" onClick={() => handleCloseConversation(conv._id)}>⏹️ Close</button>
                  </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-card">
            <h2>✅ Completed</h2>
            {conversations.filter(c => c.status !== 'active').length === 0 ? (
              <p className="no-data">No completed conversations</p>
            ) : (
              <div className="conversations-list">
                {conversations
                  .filter(c => c.status !== 'active')
                  .sort((a, b) => new Date(b.endedAt || b.startedAt) - new Date(a.endedAt || a.startedAt))
                  .map(conv => (
                  <div key={conv._id} className={`conversation-item ${!conv.coachReviewed && conv.status === 'completed' ? 'needs-review' : ''}`}>
                    <div className="conversation-header">
                      <span className="client-name">{conv.clientId?.firstName} {conv.clientId?.lastName}</span>
                      <span className={`status-badge ${conv.status}`}>{conv.status}</span>
                      {conv.flaggedForCoach && <span className="flag-badge">⚠️ Flagged</span>}
                    </div>
                    <div className="conversation-meta">
                      <span>📅 {new Date(conv.startedAt).toLocaleDateString()}</span>
                      <span>⏱️ {conv.duration || 0} min</span>
                      <span>💬 {conv.messages?.length || 0} messages</span>
                    </div>
                    {conv.summary && <div className="conversation-summary"><strong>Summary:</strong> {conv.summary}</div>}
                    {conv.keyTopics?.length > 0 && <div className="conversation-topics">{conv.keyTopics.map((topic, i) => <span key={i} className="topic-tag">{topic}</span>)}</div>}
                    <div className="conversation-actions">
                      <button className="view-btn" onClick={() => navigate(`/persona/conversation/${conv._id}`)}>👁️ View</button>
                      <button className="deactivate-btn" onClick={() => handleDeleteConversation(conv._id)}>🗑️ Delete</button>
                      {!conv.coachReviewed && conv.status === 'completed' && <button className="review-btn" onClick={() => handleMarkReviewed(conv._id)}>✅ Reviewed</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showTokenModal && (
        <div className="modal-overlay" onClick={() => setShowTokenModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>🎉 Link Generated!</h2>
            <p>Share this link with your client:</p>
            <div className="link-display">
              <input type="text" value={newTokenUrl} readOnly />
              <button className={copiedUrl === newTokenUrl ? 'copied' : ''} onClick={() => handleCopyLink(newTokenUrl)}>
                {copiedUrl === newTokenUrl ? '✓ Copied!' : '📋 Copy'}
              </button>
            </div>
            <p className="modal-note">Your client can chat with your AI. {voiceStatus === 'ready' && '🎙️ With your voice!'} You'll get an email when they finish.</p>
            <button className="close-modal-btn" onClick={() => setShowTokenModal(false)}>Done</button>
          </div>
        </div>
      )}

      {/* AI Consent Modal */}
      {showConsentModal && (
        <AIConsentModal onAccept={handleAccept} onDecline={handleDecline} />
      )}

    </div>
  );
};

export default PersonaSetup;